/* AI Risk & Safety — internal self-classification page aligned with EU AI Act */
import React from "react";
import { useComplianceCatalog } from "@/lib/compliance";
import { useLanguage } from "@/i18n/LanguageContext";
import { ShieldCheck, CheckCircle2 } from "lucide-react";
import SEO from "@/components/SEO";
import AppFooter from "@/components/AppFooter";

const MITIGATION_LABELS_BG = {
  evidence_layer: "Научна обосновка (Evidence Engine) върху всеки клиничен анализ",
  audit_logs: "Одит на всички AI изходи с хеширане на заявките",
  human_oversight_clinic: "Човешки преглед достъпен в Clinic план",
  transparency_layer: 'Публична страница „AI Прозрачност и ограничения"',
  explainability: "Evidence score dots + drill-down към източниците",
  continuous_improvement: "Непрекъснато обновяване и re-evaluation на моделите",
  contextual_disclaimers: "Контекстуални disclaimers, мащабирани спрямо риска на изхода",
};
const MITIGATION_LABELS_EN = {
  evidence_layer: "Scientific Evidence Engine attached to every clinical analysis",
  audit_logs: "Audit logs for all AI outputs with request hashing",
  human_oversight_clinic: "Human review available in the Clinic plan",
  transparency_layer: "Public \"AI Transparency & Limitations\" page",
  explainability: "Evidence score dots + drill-down to sources",
  continuous_improvement: "Continuous model updates and re-evaluation",
  contextual_disclaimers: "Contextual disclaimers scaled to output risk",
};

export default function AIRiskSafetyPage() {
  const { catalog } = useComplianceCatalog();
  const { language } = useLanguage();
  const bg = language === "bg";
  const labels = bg ? MITIGATION_LABELS_BG : MITIGATION_LABELS_EN;
  const risk = catalog?.ai_risk;

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-indigo-50">
      <SEO
        title={bg ? "AI риск и безопасност — Nutramilo" : "AI Risk & Safety — Nutramilo"}
        description={bg ? "Вътрешна класификация на риска и мерки за безопасност спрямо EU AI Act." : "Internal risk classification and safety measures aligned with the EU AI Act."}
      />
      <main className="max-w-3xl mx-auto px-4 py-10" data-testid="ai-risk-safety-page">
        <a href="/" className="text-xs text-sky-700 hover:underline">{bg ? "← Обратно" : "← Back"}</a>
        <h1 className="text-3xl font-bold text-gray-900 mt-3 mb-2 flex items-center gap-2">
          <ShieldCheck className="text-indigo-600" size={26} />
          {bg ? "AI риск и безопасност" : "AI Risk & Safety"}
        </h1>
        <p className="text-sm text-gray-600 mb-6">
          {bg
            ? "Вътрешна самооценка в съответствие с EU AI Act — прозрачна рамка за риск, надзор и мерки."
            : "Internal self-assessment aligned with the EU AI Act — a transparent framework for risk, oversight and mitigations."}
        </p>

        <section className="grid sm:grid-cols-3 gap-3 mb-8">
          <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4">
            <div className="text-[11px] font-semibold uppercase text-indigo-700 mb-1">{bg ? "Категория" : "Category"}</div>
            <div className="text-sm font-medium text-indigo-900">{bg ? "Здравна AI за решения-подкрепа" : "Health-related AI decision support"}</div>
          </div>
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
            <div className="text-[11px] font-semibold uppercase text-amber-700 mb-1">{bg ? "Ниво на риск (вътрешно)" : "Internal risk level"}</div>
            <div className="text-sm font-medium text-amber-900">{bg ? "Умерен → Висок" : "Moderate → High"}</div>
          </div>
          <div className="rounded-lg border border-sky-200 bg-sky-50 p-4">
            <div className="text-[11px] font-semibold uppercase text-sky-700 mb-1">{bg ? "EU AI Act" : "EU AI Act"}</div>
            <div className="text-sm font-medium text-sky-900">{bg ? "Health domain — high-risk alignment" : "Health domain — high-risk alignment"}</div>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">{bg ? "Мерки за безопасност" : "Safety mitigations"}</h2>
          <ul className="space-y-2">
            {(risk?.mitigations || []).map((key) => (
              <li key={key} className="flex items-start gap-2 text-sm text-gray-700">
                <CheckCircle2 size={16} className="text-emerald-600 mt-0.5 shrink-0" />
                <span>{labels[key] || key}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">{bg ? "Модел на човешки надзор" : "Human oversight model"}</h2>
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-gray-700">{bg ? "План" : "Plan"}</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-700">{bg ? "Режим" : "Mode"}</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-700">{bg ? "Специалистен преглед" : "Specialist review"}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {["free", "core", "pro", "clinic"].map(p => {
                  const info = catalog?.human_oversight?.[p] || {};
                  return (
                    <tr key={p}>
                      <td className="px-3 py-2 capitalize font-medium text-gray-900">{p}</td>
                      <td className="px-3 py-2 text-gray-700">{info.ai_only ? (bg ? "AI само" : "AI only") : (bg ? "AI + експерт" : "AI + expert")}</td>
                      <td className="px-3 py-2 text-gray-700">{info.specialist_review ? (bg ? "Да" : "Yes") : (bg ? "—" : "—")}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {catalog?.human_oversight?.clinic?.[bg ? "note_bg" : "note_en"] && (
            <p className="text-xs text-gray-600 mt-3 leading-relaxed italic">
              {catalog.human_oversight.clinic[bg ? "note_bg" : "note_en"]}
            </p>
          )}
        </section>
      </main>
      <AppFooter />
    </div>
  );
}
